import { useContext, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import { ApiContext } from '../App';
import { AuthContext } from '../App';
import { UserContext } from '../App';
import UserRecord from '../records/user';

const options = {
  retry: false
}

export const useCreateAuthToken = () => {
  const api = useContext(ApiContext);

  return useMutation(user => api.post('/auth', user), {
    onError: error => console.log(error)
  });
}

// The API answers a bad, expired, or already-used link with a 403 and
// { success: false, message }. Let 4xx resolve (so the global 403 interceptor
// installed by RequireAuth does not redirect a logged-in admin to /invalid)
// and turn anything but a 2xx carrying an auth payload into a query error.
// `linkInvalid` is set only for the API's expired/used-link rejection; other
// 4xx messages (e.g. "Account login disabled") are surfaced as-is.
const linkInvalidMessage = /invalid or expired/i;

export const getAuth = (api, token) =>
  api.get(`/auth/${token}`, { validateStatus: status => status < 500 })
    .then(response => {
      const status = response?.status;
      const ok = status >= 200 && status < 300 && Boolean(response?.data?.data);

      if (ok) return response;

      const message = response?.data?.message;
      const error = new Error(message || 'Invalid or expired login link');
      error.status = status;
      error.apiMessage = message;
      error.linkInvalid = status === 403 && (!message || linkInvalidMessage.test(message));
      throw error;
    });

// How long a consumed link's response stays cached after the validator
// unmounts. It must be non-zero: with cacheTime 0, react-query drops the query
// the moment its last observer unsubscribes, so React.StrictMode's development
// mount/unmount/remount sent the single-use token a second time and the API
// rejected it as already used. It must also be short: signing out clears
// local storage and context but not the QueryClient, so a long-lived cached
// success would let browser Back to the same link restore the session
// without asking the API.
export const authCacheTime = 1000;

// Key the query by token so a new link never reads a previous login's cached
// response, and never refetch it: the GET consumes a single-use token and
// returns a bearer JWT.
export const useGetAuth = token => {
  const api = useContext(ApiContext);

  return useQuery(['auth', token], () => getAuth(api, token), {
    ...options,
    cacheTime: authCacheTime,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
}

export const useSetAuth = data => {
  const auth = useContext(AuthContext);
  const user = useContext(UserContext);

  useEffect(() => {
    if (!auth.auth && data?.data && !user.user.id) {
      auth.setAuth(data.data.data);
      user.setUser(new UserRecord({ ...data.data.data.attributes, is_logged_in: true }));
      window.localStorage.setItem('auth', JSON.stringify(data));
    } else if (!auth.auth && data?.data && user.user.id) {
      auth.setAuth(data.data.data);
    }
  }, [data, user.user]);
}

export const useHydrateAuth = () => {
  const saved = JSON.parse(window.localStorage.getItem('auth'));

  useSetAuth(saved);

  return saved;
}
